import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { DownloadIcon } from "lucide-react";
import { FRAMEWORK_COLOR, LEVEL_TIER_COLOR, LEVEL_TIER_LABEL } from "~/pages/skills/skills.config";
import type { SkillTemplate } from "~/pages/skills/skills.types";

interface TemplateCardProps {
  template: SkillTemplate;
  onImport: (key: string) => void;
}

export function TemplateCard({ template, onImport }: Readonly<TemplateCardProps>) {
  const frameworkColor = template.framework
    ? (FRAMEWORK_COLOR[template.framework] ?? "bg-muted text-muted-foreground")
    : "bg-muted text-muted-foreground";

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-sm font-semibold">{template.name}</CardTitle>
              {template.framework && (
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em]",
                    frameworkColor,
                  )}
                >
                  {template.framework}
                </span>
              )}
              {template.dominantLevel && (
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em]",
                    LEVEL_TIER_COLOR[template.dominantLevel],
                  )}
                >
                  {LEVEL_TIER_LABEL[template.dominantLevel]}
                </span>
              )}
            </div>
            {template.description && <p className="mt-1 text-xs text-muted-foreground">{template.description}</p>}
          </div>
          <span className="shrink-0 font-heading text-lg font-bold tabular-nums text-abyss-500">
            {template.skillCount}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-1.5">
          {template.categories.map((cat) => (
            <span
              key={cat.name}
              className="flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground"
            >
              <span className="font-medium text-foreground">{cat.name}</span>
              <span>·</span>
              <span>{cat.skillCount}</span>
            </span>
          ))}
        </div>
      </CardContent>

      <CardFooter>
        <Button size="sm" className="w-full gap-2" onClick={() => onImport(template.key)}>
          <DownloadIcon className="size-3.5" />
          Import template
        </Button>
      </CardFooter>
    </Card>
  );
}
